import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { ACTUAL_CLOSING_DATE, ATTACHMENT_ID, ATTACHMENTS, CC_EMAIL, CLOSING_ACK, CLOSING_CONTACT_EMAIL, CLOSING_CONTACT_SOURCE, CLOSING_PROOF_DOCS, DELIVERY_METHOD, EMAIL_MESSAGE, EMAIL_SUBJECT, FILE_SIZE, FILE_TYPE, IS_REQUIRED, OCP_ID, PACKET_DOC_ID, PROOF_FILE, PROOF_TYPE, PROPERTY_ID, REPORTER_CONTACT, REPORTER_EMAIL, STATUS, TARGET_CLOSING_DATE, UPLOADED_STATUS } from "../../consts";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { getRequiredFields } from "@/lib/utils";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useAddOCPClosingDocumentMutation, useAddOCPClosingEmailMutation, useDeleteOCPClosingDocumentMutation, useGetClosingProofList, useGetOCPDocumentList, useGetPropertiesByCommunity, useSendOCPClosingEmailMutation, useUpdateOCPClosingDocumentMutation } from "@/services";
import { toast } from "@/components/Toast";
import { setOCPData, setPropertyData } from "@/redux/slices";
import { useQueryClient } from "@tanstack/react-query";

const useStep3 = ({onBaseSubmit}: {onBaseSubmit: any}) => {
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const ocpData = useSelector((state: RootState) => state.currentData.ocpData);
  const propertyData = useSelector((state: RootState) => state.currentData.propertyData);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const dispatch = useDispatch();
  const currAction = useRef<"save" | "email">("save");
  const deletePacketDocId = useRef<string[]>([]);
  const queryClient = useQueryClient();
  const [updatedKey, setUpdatedKey] = useState<number>(Math.random() * 2);

  const form = useForm<formValues>({
    defaultValues: {
      // step 3
      [ACTUAL_CLOSING_DATE]: undefined,
      [CLOSING_PROOF_DOCS]: [{
        [PACKET_DOC_ID]: "",
        [ATTACHMENT_ID]: "",
        [PROOF_TYPE]: "",
        [IS_REQUIRED]: false,
        [FILE_SIZE]: "",
        [FILE_TYPE]: "",
        [PROOF_FILE]: null,
        [UPLOADED_STATUS]: false
      }],
      [CLOSING_CONTACT_EMAIL]: "",
      [CC_EMAIL]: "",
      [DELIVERY_METHOD]: ATTACHMENTS,
      [EMAIL_SUBJECT]: `${communityData?.communityName} ${propertyData?.name} - Closing Packet` || "",
      [EMAIL_MESSAGE]: "",
      [CLOSING_ACK]: false,
      [STATUS]: "COMPLETED"
    },
    resolver: zodResolver(schema),
  });
  
  const requiredFields = getRequiredFields(schema);
  const formData: any = useWatch({ control: form.control });

  useEffect(() => {
    queryClient.invalidateQueries({queryKey: ["ocp-document-types", communityData?.communityId]})
    queryClient.invalidateQueries({ queryKey: ["closing-proof-list", ocpData?.[OCP_ID]]})
  }, []);

  const { data: ocpDocList, isLoading: isDocListLoading } = useGetOCPDocumentList({
    communityId: communityData?.communityId,
    ocpStage: "CLOSING_PACKAGE",
    queryParams: { enabled: !!communityData?.communityId },
  });
  
  const { data: closingProofList, isLoading: isLoadingProofList } = useGetClosingProofList({
    ocpId: ocpData?.[OCP_ID],
    ocpStage: "CLOSING_PACKAGE",
    queryParams: {
      enabled: !!ocpData?.[OCP_ID]
    }
  });

  const { data: propertyList, isLoading: isPropertyLoading } =
    useGetPropertiesByCommunity({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });
    
  useEffect(() => {
    if (propertyList?.data) {
      const propertyData = propertyList?.data?.find((data: any) => data?.propertyId == ocpData?.[PROPERTY_ID]) || "";
      dispatch(setPropertyData(propertyData));
    }
  }, [propertyList]);

  useEffect(() => {
    if (closingProofList) {
      const data = closingProofList?.data?.draftEmail;
      form.setValue(CC_EMAIL, data?.[CC_EMAIL]?.join(",") || "");
      form.setValue(DELIVERY_METHOD, data?.[DELIVERY_METHOD] || ATTACHMENTS);
      form.setValue(EMAIL_SUBJECT, data?.[EMAIL_SUBJECT] || `${communityData?.name} - ${propertyData?.name} - Closing Packet` || "");
      form.setValue(EMAIL_MESSAGE, data?.[EMAIL_MESSAGE] || "");
      form.setValue(CLOSING_ACK, data?.[CLOSING_ACK] || false);
    }
  }, [closingProofList]);

  useEffect(() => {
    if (ocpData) {
      form.setValue(CLOSING_CONTACT_EMAIL, ocpData[CLOSING_CONTACT_SOURCE] == REPORTER_CONTACT ? ocpData[REPORTER_EMAIL] : ocpData[CLOSING_CONTACT_EMAIL] || "");
      form.setValue(ACTUAL_CLOSING_DATE, ocpData[TARGET_CLOSING_DATE] || undefined);
    }
  }, [ocpData]);

  useEffect(() => {
    if (ocpDocList?.data) {
      const list = ocpDocList?.data?.filter((data: any) => data?.requirementType == "REQUIRED")?.map((data: any) => {
        const closingDoc = closingProofList?.data?.documents?.find((val: any) => val?.documentTypeId == data?.documentTypeId);
        const fileName = closingDoc?.originalFileName || closingDoc?.fileName;
        return {
          [ATTACHMENT_ID]: closingDoc?.[ATTACHMENT_ID]?.toString() || "",
          [PACKET_DOC_ID]: closingDoc?.[PACKET_DOC_ID]?.toString() || "",
          [PROOF_TYPE]: data?.documentTypeId?.toString(),
          [PROOF_FILE]: fileName ? {name: fileName, size: closingDoc?.fileSize, url: closingDoc?.presignedUrl} : null,
          [IS_REQUIRED]: data?.requirementType == "REQUIRED",
          [FILE_SIZE]: data[FILE_SIZE]?.toString() || "",
          [FILE_TYPE]: data?.[FILE_TYPE]?.length ? data?.[FILE_TYPE]?.map((val: any) => `.${val}`)?.join(",") : "",
          [UPLOADED_STATUS]: closingDoc?.[UPLOADED_STATUS] || false
        }
      });

      form.setValue(CLOSING_PROOF_DOCS, list?.length ? list : [{
        [PACKET_DOC_ID]: "",
        [ATTACHMENT_ID]: "",
        [PROOF_TYPE]: "",
        [IS_REQUIRED]: false,
        [FILE_SIZE]: "",
        [FILE_TYPE]: "",
        [PROOF_FILE]: null,
        [UPLOADED_STATUS]: false
      }]);
      setUpdatedKey(Math.random() * 3);
    }
  }, [ocpDocList, closingProofList]);

  const proofDocs = useWatch({ control: form.control, name: CLOSING_PROOF_DOCS }) || [];

  const onRemoveProof = (index: number) => () => {
    delete fileInputRefs.current[index];
    if ((proofDocs as any)?.[index]?.[PACKET_DOC_ID]) {
      deletePacketDocId.current.push((proofDocs as any)?.[index]?.[PACKET_DOC_ID])
    }
    form.setValue(
      CLOSING_PROOF_DOCS,
      proofDocs.filter((_: any, idx: number) => index !== idx)
    );
  };

  const onAddMore = () => {
    form.setValue(CLOSING_PROOF_DOCS, [
      ...proofDocs,
      { [ATTACHMENT_ID]: "",
        [PROOF_TYPE]: "",
        [IS_REQUIRED]: false,
        [FILE_SIZE]: "",
        [FILE_TYPE]: "",
        [PROOF_FILE]: null,
        [UPLOADED_STATUS]: false
      },
    ]);
  };

  const onViewAttachment = (url: string) => {
    window.open(url, "_blank");
  };

  const enabledSendEmail = useMemo(() => {
    const docs = formData[CLOSING_PROOF_DOCS] || [];
    const requiredDocs = docs.filter((data: any) => data?.[IS_REQUIRED]);
    const requiredDocsReady =
      requiredDocs.length === 0 ||
      requiredDocs.every((data: any) => data?.[PROOF_FILE]);

    return requiredDocsReady && !!formData[CLOSING_ACK];
  }, [formData[CLOSING_PROOF_DOCS], formData[CLOSING_ACK]]);

  const {
    mutateAsync: addClosingProof,
    isPending: isAddDocPending,
  } = useAddOCPClosingDocumentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const {
    mutateAsync: updateClosingProof,
    isPending: isUpdateDocPending,
  } = useUpdateOCPClosingDocumentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const {
    mutateAsync: deleteClosingProof,
    isPending: isDeleteDocPending,
  } = useDeleteOCPClosingDocumentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const {
    mutate: addEmailDraft,
    isPending: isAddEmailPending,
  } = useAddOCPClosingEmailMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const {
    mutate: sendEmail,
    isPending: isSendEmailPending,
  } = useSendOCPClosingEmailMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });
  
  const validateClosingDoc = (data: any) => {
    const docs = data[CLOSING_PROOF_DOCS] || [];

    for (const doc of docs) {
      const isRequired = Boolean(doc?.[IS_REQUIRED]);
      const hasFile = Boolean(doc?.[PROOF_FILE]);
      const hasType = Boolean(doc?.[PROOF_TYPE]);

      // Rule 1: If doc is required and proof file is missing -> Error
      if (isRequired && !hasFile) {
        toast.error("Please upload the required proof file.");
        return false;
      }

      // Rule 2: If optional (not required), type is selected, but file is missing -> Error
      // (If both type and file are empty, it passes safely as valid optional row)
      if (!isRequired && hasType && !hasFile) {
        toast.error("Please upload a proof file for the selected proof type.");
        return false;
      }
    }

    return true;
  }

  const uploadClosingDoc = async (ocpId: string, docsToUpload: any[]) => {
    const docExist = docsToUpload?.filter(
      (file: any) => file?.[PROOF_FILE] instanceof File
    ) || [];

    let uploadPromises: any[] = [];

    if (deletePacketDocId.current?.length) {
      deletePacketDocId.current?.forEach((packageDocId: string) => {
        uploadPromises.push(
          deleteClosingProof({
            ocpId: ocpId.toString(),
            packageDocId
          })
        )
      })
      
    }

    if (docExist.length) {
      docExist.map((file: any) => {
        const formData = new FormData();
        formData.append("file", file?.[PROOF_FILE]);
        formData.append("ocpStage", "CLOSING_PACKAGE");
        formData.append("documentTypeId", file?.[PROOF_TYPE]);
  
        if (file[PACKET_DOC_ID]) {
          uploadPromises.push(updateClosingProof({
            ocpId: ocpId.toString(),
            packageDocId: file?.[PACKET_DOC_ID],
            data: formData,
          }));
        } else {
          uploadPromises.push(addClosingProof({
            ocpId: ocpId.toString(),
            data: formData,
          }));
        }
      });
  
      try {
        // 1. Wait for all mutation promises to resolve
        const results = await Promise.all(uploadPromises);
  
        // 2. Extract success payload data from response
        const newlyUploadedFiles = results.map((res: any, index: number) => ({
          ...res?.data, // API response data (e.g., attachmentId, filePath, etc.)
          proofType: docExist[index]?.[PROOF_TYPE],
          fileName: docExist[index]?.[PROOF_FILE]?.name,
        }));
  
        // 3. Update React state (append to existing state)
        dispatch(setOCPData({ ...ocpData, [CLOSING_PROOF_DOCS]: [...newlyUploadedFiles] }))
  
        return newlyUploadedFiles;
      } catch (error) {
        console.error("One or more files failed to upload:", error);
        throw error;
      }
    }
  };

  const onSubmit = async (data: any) => {
    if (currAction.current == "email") {
      sendEmail({
        [OCP_ID]: ocpData[OCP_ID],
          data: {
            ...(data[CC_EMAIL]?.length ? {[CC_EMAIL]: data[CC_EMAIL]?.split(",")?.map((email: string) => email.trim()) || []} : {}),
            [DELIVERY_METHOD]: data[DELIVERY_METHOD],
            [EMAIL_SUBJECT]: data[EMAIL_SUBJECT],
            [EMAIL_MESSAGE]: data[EMAIL_MESSAGE],
            [CLOSING_ACK]: data[CLOSING_ACK],
          }
      });
    } else {
      const isClosingDocValid = validateClosingDoc(data);
      if (!isClosingDocValid) return;
  
      try {
        // Step 3 Requirement: Upload documents BEFORE calling addOwnershipChangePacket
        await uploadClosingDoc(ocpData[OCP_ID], data[CLOSING_PROOF_DOCS]);
      } catch (error) {
        console.error("Error uploading proof documents:", error);
        return; // Stop execution if document uploads fail
      }
  
      onBaseSubmit({
        [ACTUAL_CLOSING_DATE]: data[ACTUAL_CLOSING_DATE],
        [OCP_ID]: ocpData[OCP_ID],
        [STATUS]: data[STATUS],
      })
      
      if (data[CC_EMAIL] || 
        data[DELIVERY_METHOD] ||
        data[EMAIL_SUBJECT] ||
        data[EMAIL_MESSAGE] ||
        data[CLOSING_ACK]) {
          addEmailDraft({
            [OCP_ID]: ocpData[OCP_ID],
            data: {
              ...(data[CC_EMAIL]?.length ? {[CC_EMAIL]: data[CC_EMAIL]?.split(",")?.map((email: string) => email.trim()) || []} : {}),
              [DELIVERY_METHOD]: data[DELIVERY_METHOD],
              [EMAIL_SUBJECT]: data[EMAIL_SUBJECT],
              [EMAIL_MESSAGE]: data[EMAIL_MESSAGE],
              [CLOSING_ACK]: data[CLOSING_ACK],
            }
          });
        }
    }
  }

  const onSubmitClick = (action: "email" | "save") => {
    currAction.current = action;
  };

  return {
    form,
    requiredFields,
    fileInputRefs,
    proofDocs,
    onRemoveProof,
    onAddMore,
    isDocListLoading: isDocListLoading || isLoadingProofList || isPropertyLoading,
    ocpDocList: ocpDocList?.data || [],
    formData,
    onViewAttachment,
    enabledSendEmail,
    onSubmit,
    isAddDocPending: isAddDocPending || isAddEmailPending || isUpdateDocPending || isDeleteDocPending,
    isSendEmailPending,
    onSubmitClick,
    updatedKey
  };
};

export default useStep3;